export type PhotoPresentationLayout =
  | 'center'
  | 'fullWidth'
  | 'pageWidth'

  // New
  | 'monogram'
  | 'noPhoto'
  | 'photoArch'
  | 'photoBackground'
  | 'photoBackgroundMonogram'
  | 'photoDuoLeft'
  | 'photoDuoRight'
  | 'photoMarquee'
  | 'photoMarqueePageWidth'
  | 'photoSlider'
  | 'photoSliderPageWidth'
  | 'photoTrio'
  | 'photoTrioPageWidth'
  | 'videoBackground'
  | 'videoBackgroundMonogram'
  | 'videoArch';

export type SinglePhotoPresentationLayout = Extract<PhotoPresentationLayout, 'center' | 'fullWidth' | 'pageWidth'>;

export type AspectRatio =
  | number
  | number[]
  // This means the aspect ratio is fixed and should not change
  // only can change alignment of photo
  | 'locked'

  // This means the cropping can be freeform
  | null;

export type PhotoPresentationConfig = Record<
  PhotoPresentationLayout,
  {
    min: number;
    max: number;

    aspectRatio: AspectRatio;
    mobileAspectRatio?: number;

    aloha?: {
      aspectRatio: AspectRatio;
    };

    brannan?: {
      aspectRatio: AspectRatio;
    };
  }
>;

export const PHOTO_PRESENTATION_CONFIG: PhotoPresentationConfig = {
  center: {
    min: 1,
    max: 1,

    // only can change alignment of photo
    aspectRatio: 'locked', // This means the aspect ratio is fixed and should not change
  },
  fullWidth: {
    min: 1,
    max: 1,

    aspectRatio: 21 / 9,
  },
  pageWidth: {
    min: 1,
    max: 1,

    aspectRatio: 16 / 9,
  },
  photoArch: {
    min: 1,
    max: 1,

    aspectRatio: 9 / 7,
  },
  photoBackground: {
    min: 1,
    max: 1,

    aspectRatio: 3 / 2,
    mobileAspectRatio: 9 / 16,
  },
  photoBackgroundMonogram: {
    min: 1,
    max: 1,

    aspectRatio: 3 / 2,
    mobileAspectRatio: 9 / 16,
  },
  videoBackground: {
    min: 1,
    max: 1,

    aspectRatio: 3 / 2,
  },
  videoBackgroundMonogram: {
    min: 1,
    max: 1,

    aspectRatio: 3 / 2,
  },
  videoArch: {
    min: 1,
    max: 1,

    aspectRatio: 6 / 5,
  },

  // multi photo
  photoSlider: {
    min: 2,
    max: 6,
    aspectRatio: 12 / 7,

    aloha: {
      aspectRatio: 1 / 1,
    },

    brannan: {
      aspectRatio: 12 / 7,
    },
  },
  photoSliderPageWidth: {
    min: 2,
    max: 6,
    aspectRatio: 12 / 7,

    brannan: {
      aspectRatio: 12 / 7,
    },
  },
  photoMarquee: {
    min: 4,
    max: 6,

    aspectRatio: null,
  },
  photoMarqueePageWidth: {
    min: 4,
    max: 6,

    aspectRatio: null,
  },
  photoTrio: {
    min: 1,
    max: 3,

    aspectRatio: 6 / 9,
  },
  photoTrioPageWidth: {
    min: 1,
    max: 3,

    aspectRatio: 6 / 9,
  },
  photoDuoLeft: {
    min: 2,
    max: 2,

    aspectRatio: [7 / 5, 5 / 7],
  },
  photoDuoRight: {
    min: 2,
    max: 2,

    aspectRatio: [5 / 7, 7 / 5],
  },
  monogram: {
    min: 0,
    max: 0,

    aspectRatio: null,
  },
  noPhoto: {
    min: 0,
    max: 0,

    aspectRatio: null,
  },
};

const VIDEO_LAYOUTS: ReadonlyArray<PhotoPresentationLayout> = ['videoBackground', 'videoBackgroundMonogram', 'videoArch'] as const;

export const isVideoLayout = (presentationLayout: PhotoPresentationLayout | undefined): boolean => {
  return VIDEO_LAYOUTS.includes(presentationLayout as PhotoPresentationLayout);
};

// Layouts that are inherently animated (carousel / marquee motion). Used to derive the
// "Animated" gallery badge directly from the layout — there is no separate per-template flag.
const ANIMATED_LAYOUTS: ReadonlyArray<PhotoPresentationLayout> = ['photoSlider', 'photoSliderPageWidth', 'photoMarquee', 'photoMarqueePageWidth'] as const;

export const isAnimatedLayout = (presentationLayout: PhotoPresentationLayout | undefined): boolean => {
  return ANIMATED_LAYOUTS.includes(presentationLayout as PhotoPresentationLayout);
};

// These use EventNameNavForStandaloneHeader, which has no mobile title banner.
const BANNERLESS_BACKGROUND_LAYOUTS: ReadonlyArray<PhotoPresentationLayout> = ['photoBackground', 'photoBackgroundMonogram', 'videoBackground', 'videoBackgroundMonogram'] as const;

/** Whether the website's Classic Navigation header shows the mobile top banner (the registry's CoupleHeader ignores layout). Unknown layouts count as capable. */
export const isBannerCapableHeader = (presentationLayout: PhotoPresentationLayout | undefined): boolean => {
  return presentationLayout === undefined || !BANNERLESS_BACKGROUND_LAYOUTS.includes(presentationLayout);
};

/** Background layouts whose photo or video fills the whole header behind the content. */
export const isFullBleedBackgroundLayout = (presentationLayout: PhotoPresentationLayout | undefined): boolean => {
  return BANNERLESS_BACKGROUND_LAYOUTS.includes(presentationLayout as PhotoPresentationLayout);
};

const MONOGRAM_LAYOUTS: ReadonlyArray<PhotoPresentationLayout> = ['monogram', 'videoBackgroundMonogram', 'photoBackgroundMonogram'] as const;

export const isMonogramLayout = (presentationLayout: PhotoPresentationLayout | undefined): boolean => {
  return MONOGRAM_LAYOUTS.includes(presentationLayout as PhotoPresentationLayout);
};
